import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signup, googleSignIn } from '../services/api';
import toast from 'react-hot-toast';

const Signup = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    const [searchParams] = useSearchParams();
    const planParam = searchParams.get('plan');
    
    // Si viene plan=organizer o plan=player en la URL, pre-seleccionamos el rol
    const initialRole = planParam === 'organizer' ? 'organizer' : 
                       planParam === 'player' ? 'player' : null;

    // BETA ACCESS LOGIC
    const [selectedRole, setSelectedRole] = useState(initialRole); // null, 'player', 'organizer'

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'player',
        phone: '',
        gender: '',
        level: 0.5,
        laterality: '',
        position: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // VALIDACIÓN DE CONTRASEÑA
        if (formData.password !== formData.confirmPassword) {
            return toast.error('Las contraseñas no coinciden');
        }

        setLoading(true);

        try {
            const dataToSubmit = { ...formData, role: selectedRole };
            // Eliminar confirmPassword antes de enviar al API para limpieza
            delete dataToSubmit.confirmPassword;
            
            console.log('📤 [Signup] Enviando datos:', dataToSubmit);
            const data = await signup(dataToSubmit);
            console.log('✅ [Signup] Respuesta exitosa:', data);
            toast.success('¡Cuenta creada correctamente!');

            // Redirect based on role
            localStorage.setItem('userRole', data.user.role);
            localStorage.setItem('userName', `${data.user.firstName} ${data.user.lastName}`);
            localStorage.setItem('userEmail', data.user.email);

            if (data.user.role === 'super_admin') {
                navigate('/master-admin');
            } else if (data.user.role === 'organizer') {
                navigate('/checkout-subscription?plan=organizer&cycle=monthly');
            } else {
                navigate('/events');
            }
        } catch (error) {
            console.error('❌ [Signup] Error al registrarse:', error);
            const msg = error.response?.data?.message || 'Error al crear la cuenta. Inténtalo de nuevo.';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        try {
            const data = await googleSignIn(selectedRole);
            toast.success('¡Registro con Google exitoso!');

            // Redirect based on role
            if (data.user.role === 'super_admin') {
                navigate('/master-admin');
            } else if (data.user.role === 'organizer') {
                navigate('/checkout-subscription?plan=organizer&cycle=monthly');
            } else {
                navigate('/events');
            }
        } catch (error) {
            console.error('Error registrándose con Google:', error);
            const msg = error.code === 'auth/popup-closed-by-user'
                ? 'Registro cancelado'
                : 'Error al conectar con Google';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const renderRoleSelection = () => (
        <div className="space-y-8 animate-fade-in-up">
            <div className="text-center">
                <h2 className="mt-4 text-3xl font-extrabold text-white tracking-tight">
                    ¿Cómo quieres unirte?
                </h2>
                <p className="mt-2 text-slate-400 font-medium">
                    Elige tu perfil para una experiencia personalizada
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Opción Jugador */}
                <button
                    onClick={() => setSelectedRole('player')}
                    className="group relative bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-[var(--primary-brand)] hover:shadow-2xl hover:shadow-[var(--primary-brand)]/10 transition-all duration-300 text-left"
                >
                    <div className="w-16 h-16 bg-[var(--primary-brand)]/10 rounded-2xl flex items-center justify-center text-3xl mb-6 group-hover:scale-110 transition-transform shadow-inner border border-[var(--primary-brand)]/20">
                        🎾
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Soy Jugador</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-6">
                        Únete a partidas, compite en torneos, mejora tu nivel y conecta con otros jugadores.
                    </p>
                    <div className="flex items-center text-[var(--primary-brand)] font-bold text-sm uppercase tracking-widest">
                        Empezar como jugador 
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </div>
                </button>

                {/* Opción Organizador */}
                <button
                    onClick={() => setSelectedRole('organizer')}
                    className="group relative bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-[var(--primary-brand)] hover:shadow-2xl hover:shadow-[var(--primary-brand)]/10 transition-all duration-300 text-left"
                >
                    <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center text-3xl mb-6 group-hover:scale-110 transition-transform shadow-inner border border-blue-500/20">
                        📊
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Soy Organizador</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-6">
                        Gestiona tu club, organiza eventos, controla inscripciones y profesionaliza tu gestión.
                    </p>
                    <div className="flex items-center text-blue-400 group-hover:text-[var(--primary-brand)] font-bold text-sm uppercase tracking-widest transition-colors">
                        Empezar como organizador
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </div>
                </button>
            </div>
            
            <div className="text-center">
                <p className="text-sm text-slate-400">
                    ¿Ya tienes cuenta?{' '}
                    <Link to="/login" className="font-bold text-[var(--primary-brand)] hover:text-[#84d93d] transition-colors">
                        Inicia sesión
                    </Link>
                </p>
            </div>
        </div>
    );

    const renderSignupForm = () => (
        <form className="animate-fade-in-up space-y-6" onSubmit={handleSubmit}>
            <div className="bg-white/5 backdrop-blur-xl py-8 px-4 shadow-2xl rounded-[2rem] sm:px-10 border border-white/10">
                {/* Header del formulario con botón Volver */}
                <div className="flex items-center justify-between mb-8">
                    <button 
                        type="button"
                        onClick={() => setSelectedRole(null)}
                        className="flex items-center text-xs font-bold text-slate-400 hover:text-white uppercase tracking-widest transition-colors"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                        </svg>
                        Cambiar rol
                    </button>
                    <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                        selectedRole === 'player' ? 'bg-[var(--primary-brand)]/10 text-[var(--primary-brand)] border-[var(--primary-brand)]/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                    }`}>
                        Registro: {selectedRole === 'player' ? 'Jugador' : 'Organizador'}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Nombre</label>
                        <input
                            name="firstName"
                            type="text"
                            required
                            value={formData.firstName}
                            onChange={handleChange}
                            className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm transition-all"
                            placeholder="Tu nombre"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Apellidos</label>
                        <input
                            name="lastName"
                            type="text"
                            required
                            value={formData.lastName}
                            onChange={handleChange}
                            className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm transition-all"
                            placeholder="Tus apellidos"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Correo Electrónico</label>
                    <input
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm transition-all"
                        placeholder="email@ejemplo.com"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Contraseña</label>
                        <div className="relative">
                            <input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm pr-12 transition-all"
                                placeholder="••••••••"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-[var(--primary-brand)] transition-colors"
                            >
                                {showPassword ? (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                    </svg>
                                ) : (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Repetir Contraseña</label>
                        <div className="relative">
                            <input
                                name="confirmPassword"
                                type={showPassword ? "text" : "password"}
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm pr-12 transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>
                </div>

                {selectedRole === 'player' && (
                    <div className="space-y-6 pt-6 border-t border-slate-700/50 mt-2">
                         {/* DATOS DE JUEGO */}
                        <div>
                             <h4 className="text-[10px] font-black text-[var(--primary-brand)] uppercase tracking-widest mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[var(--primary-brand)] rounded-full"></span>
                                Datos de Juego
                            </h4>
                            
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Teléfono</label>
                                    <input
                                        name="phone"
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm transition-all"
                                        placeholder="+34 600 000 000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 ml-1">Género</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { id: 'male', label: 'Hombre' },
                                            { id: 'female', label: 'Mujer' },
                                            { id: 'other', label: 'Otro' }
                                        ].map(g => (
                                            <button
                                                key={g.id}
                                                type="button"
                                                onClick={() => setFormData(p => ({ ...p, gender: g.id }))}
                                                className={`py-3 px-1 rounded-xl border text-[10px] font-bold transition-all ${formData.gender === g.id ? 'bg-[var(--primary-brand)] text-white border-[var(--primary-brand)] shadow-lg shadow-[var(--primary-brand)]/20' : 'bg-slate-900/50 text-slate-400 border-slate-700 hover:border-slate-600'}`}
                                            >
                                                {g.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-1 ml-1">
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest">Nivel (Padel)</label>
                                        <span className={`px-3 py-1 rounded-xl text-xs font-black border ${
                                            (formData.level || 0.5) < 2 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                            (formData.level || 0.5) < 4 ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                            (formData.level || 0.5) < 6 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                            'bg-red-500/10 text-red-400 border-red-500/20'
                                        }`}>
                                            {parseFloat(formData.level || 0.5).toFixed(1)}
                                        </span>
                                    </div>
                                    <div className="relative pt-4 px-1">
                                        <input
                                            type="range"
                                            name="level"
                                            min="0.5"
                                            max="7.0"
                                            step="0.1"
                                            value={formData.level || 0.5}
                                            onChange={(e) => setFormData(p => ({ ...p, level: parseFloat(e.target.value) }))}
                                            className="w-full h-2.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-[var(--primary-brand)]"
                                        />
                                        <div className="flex justify-between mt-3 px-1">
                                            <span className="text-[10px] font-black text-slate-600">0.5</span>
                                            <span className="text-[10px] font-black text-slate-600">7.0</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PREFERENCIAS */}
                        <div className="pt-6 border-t border-slate-700/50 mt-6">
                            <h4 className="text-[10px] font-black text-[var(--primary-brand)] uppercase tracking-widest mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[var(--primary-brand)] rounded-full"></span>
                                Preferencias en Pista
                            </h4>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 ml-1">Lateralidad</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { id: 'right', label: 'Diestro' },
                                            { id: 'left', label: 'Zurdo' },
                                            { id: 'ambidextrous', label: 'Ambos' }
                                        ].map(l => (
                                            <button
                                                key={l.id}
                                                type="button"
                                                onClick={() => setFormData(p => ({ ...p, laterality: l.id }))}
                                                className={`py-3 px-1 rounded-xl border text-[10px] font-bold transition-all ${formData.laterality === l.id ? 'bg-[var(--primary-brand)] text-white border-[var(--primary-brand)] shadow-lg shadow-[var(--primary-brand)]/20' : 'bg-slate-900/50 text-slate-400 border-slate-700 hover:border-slate-600'}`}
                                            >
                                                {l.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 ml-1">Posición Preferida</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { id: 'drive', label: 'Drive' },
                                            { id: 'reves', label: 'Revés' },
                                            { id: 'both', label: 'Ambos' }
                                        ].map(pos => (
                                            <button
                                                key={pos.id}
                                                type="button"
                                                onClick={() => setFormData(p => ({ ...p, position: pos.id }))}
                                                className={`py-3 px-1 rounded-xl border text-[10px] font-bold transition-all ${formData.position === pos.id ? 'bg-[var(--primary-brand)] text-white border-[var(--primary-brand)] shadow-lg shadow-[var(--primary-brand)]/20' : 'bg-slate-900/50 text-slate-400 border-slate-700 hover:border-slate-600'}`}
                                            >
                                                {pos.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    className={`group relative w-full flex justify-center py-5 px-4 border border-transparent text-xs font-black rounded-2xl text-white ${
                        selectedRole === 'player' ? 'bg-[var(--primary-brand)] hover:bg-[#65a30d] shadow-[var(--primary-brand)]/30' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/30'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-lg transition-all transform active:scale-95 uppercase tracking-widest mt-8 ${loading ? 'opacity-70 cursor-wait' : ''}`}
                >
                    {loading ? 'Creando cuenta...' : 'Crear mi cuenta'}
                </button>

                <div className="mt-8 border-t border-slate-700/50 pt-8">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-700" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-[#0f172a] bg-opacity-0 text-slate-500 backdrop-blur-xl font-bold uppercase tracking-widest text-[10px]">
                                O regístrate con
                            </span>
                        </div>
                    </div>

                    <div className="mt-6">
                        <button
                            type="button"
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                            className={`w-full inline-flex justify-center py-4 px-4 border border-slate-700 rounded-xl shadow-sm bg-white text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all transform active:scale-95 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                                />
                            </svg>
                            Google
                        </button>
                    </div>
                </div>

                <div className="text-center mt-8">
                    <p className="text-sm text-slate-400 font-medium">
                        ¿Ya tienes cuenta?{' '}
                        <Link to="/login" className="font-black text-[var(--primary-brand)] hover:text-[#84d93d] transition-colors uppercase tracking-widest text-xs">
                            Inicia sesión
                        </Link>
                    </p>
                </div>
            </div>
        </form>
    );

    return (
        <div className="min-h-screen bg-[#0f172a] flex flex-col relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[var(--primary-brand)]/10 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[var(--primary-brand)]/5 rounded-full blur-[100px] pointer-events-none"></div>

            {/* Navbar */}
            <nav className="fixed top-0 left-0 w-full z-50 bg-[#0f172a]/95 backdrop-blur-md h-20 md:h-24 transition-all border-b border-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="flex justify-between items-center h-full">
                        <Link to="/" className="flex items-center group">
                            <img 
                                src="/assets/SVG/LogoVisioPadelBlanco.svg?v=2" 
                                alt="VisioPadel" 
                                className="h-14 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>

                        <div className="hidden md:flex items-center gap-8 text-[15px] font-light text-slate-200">
                            <Link to="/pricing" className="nav-link-xporty hover:text-white transition-colors">Organiza tu evento</Link>
                            <Link to="/about" className="nav-link-xporty hover:text-white transition-colors">Cómo Funciona</Link>
                            <Link to="/login" className="nav-link-xporty hover:text-white transition-colors">Entrar</Link>
                            <Link to="/signup" className="nav-link-xporty hover:text-white transition-colors active">Registrarse</Link>
                        </div>



                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 text-white"
                        >
                             <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10 pt-32">
                <div className={`${!selectedRole ? 'max-w-4xl' : 'max-w-md'} w-full space-y-12 relative z-10 transition-all duration-500`}>
                    {!selectedRole ? (
                        renderRoleSelection()
                    ) : (
                        renderSignupForm()
                    )}
                </div>
            </div>
        </div>
    );
};

export default Signup;
